import { useCallback } from 'react';

export interface PickedImage {
  uri: string;
  name: string;
  type: string;
  width: number;
  height: number;
  file?: File; // 웹 picker가 보존한 File 객체 (RN은 undefined)
}

interface GetImagesParams {
  currentSize: number;
  maxPhotoSize?: number;
}

const pickFiles = ({
  multiple,
  capture,
}: {
  multiple?: boolean;
  capture?: 'environment' | 'user';
}): Promise<File[]> =>
  new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (multiple === true) input.multiple = true;
    if (capture != null) input.capture = capture;

    // iOS Safari 는 문서에 없는 input 에 사진 보관함 결과를 전달하지 않는다.
    // display:none 은 피커가 안 열리는 사례가 있어 보이지 않게만 둔다.
    input.style.position = 'fixed';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';
    document.body.appendChild(input);

    const finish = (files: File[]) => {
      input.remove();
      resolve(files);
    };
    input.addEventListener('change', () =>
      finish(Array.from(input.files ?? [])),
    );
    input.addEventListener('cancel', () => finish([]));
    input.click();
  });

// uri 는 미리보기 전용(업로드는 file 을 쓴다). base64 는 사진을 문자열로 복사해
// iOS Safari 가 메모리 압박으로 리로드되므로 blob URL 을 쓴다.
const toPickedImage = (file: File): PickedImage => ({
  uri: URL.createObjectURL(file),
  name: file.name,
  type: file.type || 'image/jpeg',
  width: 0,
  height: 0,
  file,
});

export const useImagePicker = () => {
  const takePhoto = useCallback(async (): Promise<PickedImage[]> => {
    const files = await pickFiles({ capture: 'environment' });
    return files.map(toPickedImage);
  }, []);

  const getImages = useCallback(
    async ({
      currentSize,
      maxPhotoSize,
    }: GetImagesParams): Promise<PickedImage[]> => {
      const remaining =
        maxPhotoSize != null ? maxPhotoSize - currentSize : undefined;
      const files = await pickFiles({
        multiple: maxPhotoSize == null || maxPhotoSize > 1,
      });
      const limited = remaining != null ? files.slice(0, remaining) : files;
      return limited.map(toPickedImage);
    },
    [],
  );

  return { getImages, takePhoto };
};
